import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import ModalOrDrawer from "ui/ModalOrDrawer";
import { Button } from "ui/Button";
import EmptyState from "ui/EmptyState";
import { promotionCreditQueryKeys, usePromotionCreditBalanceQuery } from "hooks/queries/useAwardBalanceQuery";
import { useUser } from "util/UserContext";
import { getAdBreakHref } from "util/adBreakUrl";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import {
  PromotionApiError,
  promotionsApi,
  type PromotionCampaign,
  type PromotionSponsorActorType,
} from "lib/api/promotions";

const IMPRESSIONS_PER_CREDIT = 10;

const formatTemplate = (template: string, values: Record<string, string | number>): string =>
  Object.entries(values).reduce(
    (acc, [key, value]) => acc.split(`{${key}}`).join(String(value)),
    template
  );

export interface PromoteEntityModalProps {
  /** Controlled open state. */
  isOpen: boolean;
  /** Called when the modal requests close (cancel or success). */
  onClose: () => void;
  /** Target entity kind: `'post' | 'comment' | 'user' | 'team' | ...`. */
  entityType: string;
  /** Target entity id. */
  entityId: number;
  /** Optional display name for the target, used in the modal title. */
  entityLabel?: string | null;
  /**
   * Sponsor descriptor. When omitted, defaults to the current user's own
   * balance. For team promotions, pass
   * `{ actorType: 'team', actorId: <teamId> }` with a caller who is a
   * moderator of that team.
   */
  sponsor?: {
    actorType: PromotionSponsorActorType;
    actorId: number;
    label?: string;
    balance?: number | null;
  };
  /** Called after a successful start; receives the new/extended campaign. */
  onCampaignStarted?: (campaign: PromotionCampaign) => void;
}

/**
 * Promote flow modal. Shares the {@link ModalOrDrawer} surface so it
 * renders as a Radix modal on desktop and a Vaul bottom sheet on mobile.
 *
 * Responsibilities:
 *  - Fetch the caller's actor balance and the existing campaign (if any).
 *  - Collect a token stake (min 1, max balance).
 *  - Submit through the promotions API with an idempotent key.
 *  - Invalidate cached balance + campaign data on success.
 */
const PromoteEntityModal: React.FC<PromoteEntityModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityLabel,
  sponsor,
  onCampaignStarted,
}) => {
  const { currentUser } = useUser();
  const strings = getStoryUiStrings(resolveStoryLocale());
  const queryClient = useQueryClient();

  const sponsorActorType: PromotionSponsorActorType = sponsor?.actorType ?? "user";
  const sponsorActorId = sponsor?.actorId ?? currentUser?.id ?? null;

  const userBalanceQuery = usePromotionCreditBalanceQuery(Boolean(currentUser) && sponsorActorType === "user");
  const balance = useMemo(() => {
    if (sponsorActorType === "team") {
      return typeof sponsor?.balance === "number" ? sponsor.balance : 0;
    }
    return userBalanceQuery.data?.balance ?? 0;
  }, [sponsor?.balance, sponsorActorType, userBalanceQuery.data?.balance]);

  const existingCampaignQuery = useQuery({
    queryKey: ["promotionCampaign", entityType, entityId],
    queryFn: () => promotionsApi.getForTarget(entityType, entityId),
    enabled: isOpen && Boolean(entityType) && Boolean(entityId),
    staleTime: 10_000,
  });

  const [creditsToStake, setCreditsToStake] = useState<number>(1);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCreditsToStake((prev) => {
        if (prev < 1) return 1;
        if (balance > 0 && prev > balance) return balance;
        return prev;
      });
      setFormError(null);
    }
  }, [balance, isOpen]);

  const campaignMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser) {
        throw new Error("PROMOTION_REQUIRES_AUTH");
      }
      if (!sponsorActorId) {
        throw new Error("PROMOTION_INVALID_SPONSOR");
      }
      return promotionsApi.startCampaign({
        targetType: entityType,
        targetId: entityId,
        creditsStaked: creditsToStake,
        sponsorActorType,
        sponsorActorId,
        idempotencyKey:
          `promote:${sponsorActorType}:${sponsorActorId}:${entityType}:${entityId}:${Date.now()}`,
      });
    },
    onSuccess: async (campaign) => {
      toast.success(strings.promoteSuccess);
      await queryClient.invalidateQueries({ queryKey: ["promotionCampaign", entityType, entityId] });
      await queryClient.invalidateQueries({ queryKey: promotionCreditQueryKeys.balance() });
      onCampaignStarted?.(campaign);
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof PromotionApiError) {
        if (error.code === "AWARD_BALANCE_INSUFFICIENT" || error.status === 409) {
          setFormError(strings.promoteInsufficientBalance);
          return;
        }
        if (error.code === "PROMOTION_TARGET_NOT_PUBLIC") {
          setFormError(strings.promoteNotEligibleNotPublic);
          return;
        }
        if (error.code === "PROMOTION_TARGET_PRIVATE") {
          setFormError(strings.promoteNotEligiblePrivate);
          return;
        }
        if (error.code === "PROMOTION_TARGET_SETTING_OFF") {
          setFormError(strings.promoteNotEligibleSettingOff);
          return;
        }
      }
      setFormError(strings.promoteError);
    },
  });

  const handleConfirm = useCallback(() => {
    if (creditsToStake < 1) {
      setFormError(strings.promoteInsufficientBalance);
      return;
    }
    if (creditsToStake > balance) {
      setFormError(strings.promoteInsufficientBalance);
      return;
    }
    campaignMutation.mutate();
  }, [balance, campaignMutation, creditsToStake, strings.promoteInsufficientBalance]);

  const existingCampaign = existingCampaignQuery.data;
  const extendCopy = existingCampaign
    ? formatTemplate(strings.promoteExtendExisting, {
        remaining: existingCampaign.impressions_remaining,
      })
    : null;

  const title = entityLabel
    ? `${strings.promoteModalTitle} — ${entityLabel}`
    : strings.promoteModalTitle;

  const confirmDisabled = creditsToStake < 1
    || creditsToStake > balance
    || campaignMutation.isPending
    || userBalanceQuery.isPending;

  const footer = (
    <div className="flex items-center justify-end gap-2 border-t border-eoe-border-main px-4 py-3 dark:border-eoe-dark-border-main">
      <Button variant="ghost" onClick={onClose} disabled={campaignMutation.isPending}>
        {strings.promoteCancelButton}
      </Button>
      <Button
        variant="default"
        onClick={handleConfirm}
        disabled={confirmDisabled}
        aria-busy={campaignMutation.isPending}
      >
        <Megaphone className="mr-2 size-4" aria-hidden="true" />
        {formatTemplate(strings.promoteConfirmButton, { count: creditsToStake })}
      </Button>
    </div>
  );

  return (
    <ModalOrDrawer
      isOpen={isOpen}
      onClose={campaignMutation.isPending ? () => undefined : onClose}
      title={title}
      footer={footer}
    >
      <div className="flex flex-col gap-4 px-4 py-4 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        <p className="text-eoe-text-main dark:text-eoe-dark-text-main">
          {strings.promoteModalSubtitle}
        </p>
        {balance <= 0 ? (
          <EmptyState
            title={strings.promoteInsufficientBalance}
            icon={<TrendingUp className="size-8" aria-hidden="true" />}
            cta={
              <Button type="button" onClick={() => window.location.assign(getAdBreakHref())}>
                {strings.awardStartAdBreak}
              </Button>
            }
          />
        ) : (
          <>
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium">{strings.promoteStakeLabel}</span>
              <input
                type="number"
                min={1}
                max={Math.max(balance, 1)}
                value={creditsToStake}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  setCreditsToStake(Number.isFinite(next) ? Math.max(1, Math.min(balance, next)) : 1);
                  setFormError(null);
                }}
                aria-describedby="promote-impressions-preview"
                className="rounded-md border border-eoe-border-main bg-eoe-bg px-3 py-2 text-base text-eoe-text-main focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg dark:text-eoe-dark-text-main"
              />
            </label>
            <p
              id="promote-impressions-preview"
              className="text-sm text-eoe-text-faint dark:text-eoe-dark-text-faint"
            >
              {formatTemplate(strings.promoteImpressionsPreview, {
                count: creditsToStake * IMPRESSIONS_PER_CREDIT,
              })}
            </p>
            <p className="text-xs text-eoe-text-faint dark:text-eoe-dark-text-faint">
              {formatTemplate(strings.promoteBalanceLabel, { count: balance })}
              {sponsor?.label ? ` · ${sponsor.label}` : ""}
            </p>
            {extendCopy ? (
              <p className="rounded-md border border-amber-400/40 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-400/30 dark:bg-amber-900/20 dark:text-amber-100">
                {extendCopy}
              </p>
            ) : null}
          </>
        )}
        {formError ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {formError}
          </p>
        ) : null}
      </div>
    </ModalOrDrawer>
  );
};

export default PromoteEntityModal;
